// CourseStatusModal.tsx
import React, { useState } from 'react';
import './CourseStatusModal.css';
import { X } from 'lucide-react';

interface CourseStatusModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (status: string) => void;
  currentStatus: string;
  courseName: string;
  isLoading?: boolean;
}

const CourseStatusModal: React.FC<CourseStatusModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  currentStatus,
  courseName,
  isLoading = false,
}) => {
  const [selectedStatus, setSelectedStatus] = useState(currentStatus);

  if (!isOpen) return null;

  const statusOptions = [
    { value: 'active', label: 'Active', description: 'Course is published and visible to learners' },
    { value: 'inactive', label: 'Inactive', description: 'Course is hidden from learners but not deleted' },
    { value: 'pending', label: 'Pending', description: 'Course is awaiting review or approval' },
  ];

  const handleConfirm = () => {
    if (selectedStatus !== currentStatus) {
      onConfirm(selectedStatus);
    }
  };

  return (
    <div className="course-status-modal-overlay" onClick={onClose}>
      <div className="course-status-modal" onClick={(e) => e.stopPropagation()}>
        <div className="course-status-modal-header">
          <h2>Change Course Status</h2>
          <button className="course-status-modal-close" onClick={onClose} disabled={isLoading}>
            <X size={24} />
          </button>
        </div>

        <div className="course-status-modal-body">
          <p className="course-status-modal-course-name">
            <strong>{courseName}</strong>
          </p>
          <p className="course-status-modal-description">
            Select the new status for this course:
          </p>

          <div className="course-status-options">
            {statusOptions.map((option) => (
              <label
                key={option.value}
                className={`course-status-option ${
                  selectedStatus === option.value ? 'selected' : ''
                }`}
              >
                <input
                  type="radio"
                  name="status"
                  value={option.value}
                  checked={selectedStatus === option.value}
                  onChange={(e) => setSelectedStatus(e.target.value)}
                  disabled={isLoading}
                />
                <div className="course-status-option-content">
                  <div className="course-status-option-label">{option.label}</div>
                  <div className="course-status-option-description">{option.description}</div>
                </div>
              </label>
            ))}
          </div>
        </div>

        <div className="course-status-modal-footer">
          <button
            className="course-status-modal-btn course-status-modal-btn-cancel"
            onClick={onClose}
            disabled={isLoading}
          >
            Cancel
          </button>
          <button
            className="course-status-modal-btn course-status-modal-btn-confirm"
            onClick={handleConfirm}
            disabled={isLoading || selectedStatus === currentStatus}
          >
            {isLoading ? 'Updating...' : 'Update Status'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CourseStatusModal;
